<script lang="ts">
  import { onMount } from 'svelte';
  import { apiClient } from '@/utils/api';
  import TopBar from './TopBar.svelte';
  import SectionNav from './design/SectionNav.svelte';
  import RadioCardGroup, { type RadioOption } from './design/RadioCardGroup.svelte';
  import ContrastBadge from './design/ContrastBadge.svelte';
  import { patternDataUrl, resolveTheme, type ProfileThemeName } from '@/lib/profile-themes';
  import {
    presets,
    profile,
    theme,
    themeDirty,
    canUndoTheme,
    canRedoTheme,
    updateThemeField,
    mergeTheme,
    resetThemeDirty,
    undoTheme,
    redoTheme,
    wallpaperLibrary,
    themeOptions,
  } from '../stores/editor';
  import type { EditorTheme, ThemePreset } from '../types';

  type ThemeResponse = {
    page_theme?: EditorTheme;
    profile?: { page_theme?: EditorTheme };
    data?: { page_theme?: EditorTheme; profile?: { page_theme?: EditorTheme } };
  };

  type EnhanceThemeResponse = {
    success?: boolean;
    page_theme?: EditorTheme;
    meta?: {
      remaining_attempts?: number;
      source?: string;
    };
    message?: string;
    error?: string;
    errors?: unknown;
  };

  // Section IA — colors used to live in their own "Colors" tab AND in
  // each per-domain tab (Wall has bg_color, Text has text/title, Buttons
  // has button colors). The duplication confused users into thinking the
  // editor was inconsistent when in fact both pickers wrote the same
  // store field. Each color now lives in exactly one tab — the one where
  // its semantic context is obvious.
  const sections = [
    { id: 'header', label: 'Header', icon: '◉' },
    { id: 'theme', label: 'Theme', icon: 'Aa' },
    { id: 'wallpaper', label: 'Wall', icon: '▒' },
    { id: 'text', label: 'Text', icon: 'T' },
    { id: 'buttons', label: 'Buttons', icon: '▣' },
    { id: 'footer', label: 'Footer', icon: '─' },
  ] as const;

  // 허용값은 서버가 정본이다 — `PageTheme.options_catalog` → `theme_options` prop → `themeOptions` store.
  // 여기에 남는 것은 라벨·설명 같은 UI 카피뿐이고, 목록 자체는 서버가 준 값으로 그린다.
  // 서버가 없는 값을 보내면 라벨이 없어도 값 그대로 그려서 선택지가 조용히 사라지지 않는다.
  const optionLabels: Record<string, Record<string, { label: string; description?: string }>> = {
    button_variant: {
      solid: { label: 'Solid', description: '가장 기본적인 꽉 찬 버튼' },
      glass: { label: 'Glass', description: '반투명 표면과 blur' },
      outline: { label: 'Outline', description: '강조색 테두리 중심' },
    },
    button_radius_preset: {
      square: { label: 'Square' },
      round: { label: 'Round' },
      rounder: { label: 'Rounder' },
      full: { label: 'Full' },
    },
    button_shadow: {
      none: { label: 'None' },
      soft: { label: 'Soft' },
      strong: { label: 'Strong' },
      hard: { label: 'Hard' },
    },
    wallpaper_style: {
      fill: { label: 'Fill' },
      gradient: { label: 'Gradient' },
      blur: { label: 'Blur' },
      pattern: { label: 'Pattern' },
      image: { label: 'Image' },
    },
    wallpaper_pattern: {
      grid: { label: 'Grid' },
      morph: { label: 'Morph' },
      organic: { label: 'Organic' },
      matrix: { label: 'Matrix' },
    },
    title_size: {
      small: { label: 'Small' },
      large: { label: 'Large' },
    },
    header_layout: {
      classic: { label: 'Classic' },
      hero: { label: 'Hero (더 큰 타이포)' },
    },
    title_style: {
      text: { label: 'Text' },
      logo: { label: 'Logo' },
    },
  };

  function radioOptions(key: string, values: string[] | undefined): RadioOption[] {
    return (values ?? []).map((value) => ({ value, ...(optionLabels[key]?.[value] ?? { label: value }) }));
  }

  // 🔴 **저장된 값이 목록에 없을 수 있다.** 시드 프리셋은 픽커와 다른 어휘를 쓴다
  //    (`SpaceGrotesk` · `GowunBatang` 등 — `PageTheme::FONT_HEADING_OPTIONS` 주석).
  //    그 값을 목록에 넣어 주지 않으면 `<select>` 가 현재 값을 표현하지 못해 **다른
  //    폰트가 선택된 것처럼 보이고**, 사용자가 저장하는 순간 실제로 바뀐다.
  //    폰트는 닫힌 값 집합이 아니므로(서버도 형식만 검증) 이 자리에서 받아들인다.
  function withCurrent(options: string[], current?: string | null): string[] {
    if (!current || options.includes(current)) return options;
    return [current, ...options];
  }

  // 🔴 프리셋 갤러리 미리보기는 시드 데이터(`theme_presets.settings`)를 **인라인 style 에
  //    직접 보간한다.** 서버는 폰트를 형식만 검증하고(`PageTheme::FONT_NAME_FORMAT`),
  //    그 검증은 `page_themes` 칼럼에만 걸린다 — 프리셋 settings JSONB 는 거치지 않는다.
  //    따옴표·세미콜론이 든 이름이 들어오면 `style` 선언을 닫고 자기 선언을 덧붙일 수 있다.
  //    공개 페이지 쪽은 서버 직렬화와 `profile-themes.ts` 가 이미 막고 있고, 여기가 남은 자리다.
  const FONT_NAME_FORMAT = /^[\p{L}\p{N}][\p{L}\p{N} \-_.]{0,63}$/u;
  function safeFontName(value: unknown): string | null {
    return typeof value === 'string' && FONT_NAME_FORMAT.test(value) ? value : null;
  }
  const headingFontOptions = $derived(withCurrent($themeOptions.font_heading ?? [], $theme.font_heading));
  const bodyFontOptions = $derived(withCurrent($themeOptions.font_body ?? [], $theme.font_body));
  const buttonVariantOptions = $derived(radioOptions('button_variant', $themeOptions.button_variant));
  const radiusOptions = $derived(radioOptions('button_radius_preset', $themeOptions.button_radius_preset));
  const shadowOptions = $derived(radioOptions('button_shadow', $themeOptions.button_shadow));
  const wallpaperOptions = $derived(radioOptions('wallpaper_style', $themeOptions.wallpaper_style));
  const patternOptions = $derived(radioOptions('wallpaper_pattern', $themeOptions.wallpaper_pattern));
  const titleSizeOptions = $derived(radioOptions('title_size', $themeOptions.title_size));
  const headerLayoutOptions = $derived(radioOptions('header_layout', $themeOptions.header_layout));
  const titleStyleOptions = $derived(radioOptions('title_style', $themeOptions.title_style));
  // 슬라이더 범위도 서버 상수(PageTheme::CARD_OPACITY_MIN/MAX)를 따른다. 하드코딩된 0.7~0.95 는
  // 서버가 범위를 바꾸는 순간 거짓말이 된다. 목록과 같은 규칙 — 로컬 사본을 두지 않는다.
  const cardOpacityRange = $derived($themeOptions.card_opacity);
  const gradientDirectionOptions = [
    { value: 90, label: 'Left → Right' },
    { value: 135, label: 'Top Left → Bottom Right' },
    { value: 180, label: 'Top → Bottom' },
    { value: 225, label: 'Top Right → Bottom Left' },
    { value: 315, label: 'Bottom Left → Top Right' },
  ];
  const customizableCategories = new Set(['minimal', 'business', 'creative', 'nature', 'lifestyle']);

  let saving = $state(false);
  let enhancing = $state(false);
  let message = $state('');
  let error = $state('');
  let activeSection = $state('theme');
  let presetTab = $state<'customizable' | 'curated'>('customizable');
  let advancedWallpaper = $state(false);

  const visiblePresets = $derived(
    presetTab === 'customizable'
      ? $presets.filter((preset) => customizableCategories.has(preset.category))
      : $presets
  );

  // Advanced card surface (card_bg_color + card_opacity) only produces a visible
  // frosted glass effect when there's layered depth behind the cards. On a flat
  // Fill background or a Pattern without translucency the opacity slider feels
  // dead, so we hide it to avoid confusing users. See REPLICA_SPEC.md §4.2.3.
  const advancedCardSurfaceEnabled = $derived(
    ['gradient', 'blur', 'image'].includes($theme.wallpaper_style || 'fill')
  );

  // For contrast checking we approximate the page background with bg_color when
  // the user picked a non-flat wallpaper. We surface a short note in those cases
  // because gradient/image/pattern/blur backgrounds can shift the effective
  // contrast away from the static value.
  const effectivePageBg = $derived($theme.bg_color || '#ffffff');
  const contrastNote = $derived.by(() => {
    const style = $theme.wallpaper_style || 'fill';
    if (style === 'gradient') return '※ gradient 배경에서는 위치별 대비가 다를 수 있습니다';
    if (style === 'image') return '※ 이미지 배경 위에서는 대비가 픽셀별로 달라집니다';
    if (style === 'blur') return '※ blur 위 카드 영역 기준 대비입니다';
    if (style === 'pattern') return '※ 패턴은 대비에 큰 영향이 없습니다';
    return '';
  });

  // Header inline-edit buffer — writes land in the profile store on blur so the
  // single global Save button can dispatch both theme + profile payloads.
  let profileDraft = $state({ display_name: '', bio: '' });
  let profileDirty = $state(false);

  $effect(() => {
    if (!profileDirty) {
      profileDraft = {
        display_name: $profile.display_name || '',
        bio: $profile.bio || '',
      };
    }
  });

  function markProfileDirty() {
    profileDirty = true;
  }

  function commitProfileDraft() {
    if (!profileDirty) return;
    profile.update((current) => ({
      ...current,
      display_name: profileDraft.display_name,
      bio: profileDraft.bio,
    }));
  }

  function responseTheme(response: ThemeResponse) {
    return response.page_theme ||
      response.profile?.page_theme ||
      response.data?.page_theme ||
      response.data?.profile?.page_theme ||
      null;
  }

  function updateColor(key: keyof EditorTheme, value: string) {
    updateThemeField(key, value || null);
    message = '';
    error = '';
  }

  function parseError(response: { error?: string; message?: string; errors?: unknown }, fallback: string) {
    if (typeof response.error === 'string' && response.error) return response.error;
    if (typeof response.message === 'string' && response.message) return response.message;
    if (response.errors && typeof response.errors === 'object') {
      const first = Object.values(response.errors as Record<string, unknown>)[0];
      if (Array.isArray(first)) return String(first[0] || fallback);
      if (typeof first === 'string') return first;
    }
    return fallback;
  }

  function parseGradientControls(gradient?: string | null) {
    const fallback = {
      angle: 135,
      from: '#1d4ed8',
      to: '#7c3aed',
    };

    if (!gradient) return fallback;

    const angleMatch = gradient.match(/(-?\d+(?:\.\d+)?)deg/i);
    const hexMatches = gradient.match(/#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})/g) || [];

    return {
      angle: angleMatch ? Number(angleMatch[1]) : fallback.angle,
      from: hexMatches[0] || fallback.from,
      to: hexMatches[1] || fallback.to,
    };
  }

  function buildLinearGradient(angle: number, from: string, to: string) {
    return `linear-gradient(${angle}deg, ${from} 0%, ${to} 100%)`;
  }

  function updateGradientControl(key: 'angle' | 'from' | 'to', value: number | string) {
    const current = parseGradientControls($theme.bg_gradient);
    const next = {
      angle: key === 'angle' ? Number(value) : current.angle,
      from: key === 'from' ? String(value) : current.from,
      to: key === 'to' ? String(value) : current.to,
    };
    updateThemeField('bg_gradient', buildLinearGradient(next.angle, next.from, next.to));
  }

  function themePayload(currentTheme: EditorTheme) {
    return {
      template: currentTheme.template || 'clean',
      button_variant: currentTheme.button_variant || 'solid',
      button_radius_preset: currentTheme.button_radius_preset || 'rounder',
      button_shadow: currentTheme.button_shadow || 'none',
      bg_color: currentTheme.bg_color || null,
      text_color: currentTheme.text_color || null,
      title_color: currentTheme.title_color || currentTheme.text_color || null,
      accent_color: currentTheme.accent_color || null,
      bg_image_url: currentTheme.bg_image_url || null,
      wallpaper_style: currentTheme.wallpaper_style || 'fill',
      wallpaper_pattern: currentTheme.wallpaper_pattern || null,
      custom_css: currentTheme.custom_css || {},
      font_heading: currentTheme.font_heading || null,
      font_body: currentTheme.font_body || null,
      button_color: currentTheme.button_color || null,
      button_text_color: currentTheme.button_text_color || null,
      bg_gradient: currentTheme.bg_gradient || null,
      card_bg_color: currentTheme.card_bg_color || null,
      card_opacity: currentTheme.card_opacity ?? 0.88,
      spacing: currentTheme.spacing || null,
      hide_branding: currentTheme.hide_branding ?? false,
      social_icon_style: currentTheme.social_icon_style || null,
      badge_color: currentTheme.badge_color || 'blue',
      header_layout: currentTheme.header_layout || 'classic',
      title_style: currentTheme.title_style || 'text',
      title_size: currentTheme.title_size || 'small',
      title_font_override: currentTheme.title_font_override ?? false,
      theme_preset_id: currentTheme.theme_preset_id || null,
    };
  }

  async function saveTheme() {
    saving = true;
    message = '';
    error = '';

    // Header section edits land in the profile store on blur; make sure the
    // store is in sync before building the save payload.
    commitProfileDraft();

    // User-level profile columns: `name` is the backing column (profile_json
    // aliases it as display_name for the frontend). `bio` is stored directly.
    const profilePayload: Record<string, unknown> = {
      page_theme: themePayload($theme),
    };
    if (profileDirty) {
      profilePayload.name = profileDraft.display_name;
      profilePayload.bio = profileDraft.bio;
    }

    try {
      const response = await apiClient.patch<ThemeResponse>(
        '/api/v1/my_profile',
        { profile: profilePayload },
        { skipCaseConversion: true }
      );

      const nextTheme = responseTheme(response);
      if (response.success && nextTheme) {
        mergeTheme(nextTheme, false);
        resetThemeDirty();
        profileDirty = false;
        message = '디자인 설정을 저장했습니다.';
      } else {
        error = parseError(response, '디자인 설정을 저장하지 못했습니다.');
      }
    } finally {
      saving = false;
    }
  }

  async function enhanceTheme() {
    enhancing = true;
    message = '';
    error = '';

    commitProfileDraft();

    const profilePayload: Record<string, unknown> = {
      name: profileDraft.display_name,
      bio: profileDraft.bio,
      page_theme: themePayload($theme),
    };

    try {
      const response = await apiClient.post<EnhanceThemeResponse>(
        '/api/v1/my_profile/enhance_theme',
        { profile: profilePayload },
        { skipCaseConversion: true }
      );

      const nextTheme = response.data?.page_theme;
      if (response.success && nextTheme) {
        mergeTheme(nextTheme);
        const remaining = response.data?.meta?.remaining_attempts;
        const suffix = typeof remaining === 'number' ? ` 남은 횟수 ${remaining}회.` : '';
        message = `AI Enhance가 디자인 제안을 적용했습니다.${suffix}`;
      } else {
        error = parseError(response, 'AI Enhance를 실행하지 못했습니다.');
      }
    } finally {
      enhancing = false;
    }
  }

  function selectSection(sectionId: string) {
    activeSection = sectionId;
    if (typeof window !== 'undefined') window.location.hash = sectionId;
  }

  // Clean replace — every theme field that a preset can control is reset to null
  // before the preset's settings are layered on top. This prevents the
  // "Frankenstein theme" bug where switching presets leaks bg_image_url, accent,
  // button_color, etc. from the previously-applied preset.
  // Identity fields (id, template, css_variables, custom_css) are preserved from
  // current state since they aren't part of preset definitions.
  const PRESET_RESETTABLE_KEYS: (keyof EditorTheme)[] = [
    'bg_color', 'bg_gradient', 'bg_image_url',
    'wallpaper_style', 'wallpaper_pattern',
    'text_color', 'title_color', 'accent_color',
    'button_color', 'button_text_color',
    'card_bg_color', 'card_opacity',
    'font_heading', 'font_body',
    'button_variant', 'button_radius_preset', 'button_shadow',
    'spacing', 'social_icon_style', 'header_layout',
    'title_style', 'title_size', 'title_font_override',
    'badge_color',
  ];

  function applyPresetLocally(preset: ThemePreset) {
    const settings = preset.settings as Partial<EditorTheme>;
    const blank: Partial<EditorTheme> = {};
    for (const key of PRESET_RESETTABLE_KEYS) {
      (blank as Record<string, unknown>)[key] = null;
    }
    mergeTheme({
      ...blank,
      template: preset.template_key || settings.template || 'clean',
      ...settings,
      theme_preset_id: preset.id,
      // Sensible defaults when the preset omits these — every preset must have
      // a button shape and a wallpaper style or the renderer falls through.
      button_radius_preset: settings.button_radius_preset || 'rounder',
      button_variant: settings.button_variant || 'solid',
      button_shadow: settings.button_shadow || 'none',
      wallpaper_style: settings.wallpaper_style || (settings.bg_image_url ? 'image' : settings.bg_gradient ? 'gradient' : 'fill'),
      title_color: (settings.title_color as string) || (settings.text_color as string) || null,
    });
    message = `"${preset.name}" 프리셋을 미리보기에 적용했습니다. Save를 눌러 저장하세요.`;
    error = '';
  }

  // Switching wallpaper style is a multi-field operation. Setting the style alone
  // leaves the actual rendering field (bg_gradient / bg_image_url / wallpaper_pattern)
  // empty, so the phone preview doesn't change and users think the click failed.
  // Seed the required field with a sensible default the first time the user
  // visits a style; preserve their value if they already have one.
  function changeWallpaperStyle(value: string) {
    const patch: Partial<EditorTheme> = { wallpaper_style: value };
    if (value === 'gradient' && !$theme.bg_gradient) {
      patch.bg_gradient = 'linear-gradient(135deg, #1d4ed8 0%, #7c3aed 100%)';
    } else if (value === 'pattern' && !$theme.wallpaper_pattern) {
      // 씨앗값도 카탈로그의 첫 항목을 쓴다. 리터럴로 두면 서버가 목록을 바꿨을 때
      // 편집기가 서버에 없는 패턴을 먼저 저장한다.
      const [firstPattern] = $themeOptions.wallpaper_pattern ?? [];
      if (firstPattern) patch.wallpaper_pattern = firstPattern;
    } else if (value === 'fill' && !$theme.bg_color) {
      patch.bg_color = '#FFFFFF';
    } else if (value === 'blur' && !$theme.bg_color) {
      patch.bg_color = '#E2E8F0';
    }
    // 'image': bg_image_url must come from the user (URL or upload), don't seed
    mergeTheme(patch);
  }

  function onBeforeUnload(event: BeforeUnloadEvent) {
    if (!$themeDirty && !profileDirty) return;
    event.preventDefault();
    event.returnValue = '';
  }

  function onKeydown(event: KeyboardEvent) {
    if (!(event.metaKey || event.ctrlKey)) return;
    if (event.key.toLowerCase() !== 'z') return;

    event.preventDefault();
    if (event.shiftKey) {
      redoTheme();
    } else {
      undoTheme();
    }
  }

  onMount(() => {
    if (typeof window === 'undefined') return undefined;

    const fromHash = window.location.hash.replace('#', '');
    if (sections.some((section) => section.id === fromHash)) activeSection = fromHash;
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('keydown', onKeydown);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('keydown', onKeydown);
    };
  });

  // Preview rendering moved to {#snippet} blocks below — see wallpaperPreview /
  // buttonPreview / radiusPreview / shadowPreview snippets. Snippets are reactive
  // and type-safe, replacing the earlier `(option) => string` + `{@html}` form.
</script>

{#snippet wallpaperPreview(option: RadioOption)}
  {#if option.value === 'gradient'}
    <div class="h-10 rounded-2xl" style="background:linear-gradient(135deg,#1d4ed8,#7c3aed);"></div>
  {:else if option.value === 'blur'}
    <div class="h-10 rounded-2xl border border-white/40" style="background:linear-gradient(135deg,rgba(148,163,184,0.55),rgba(226,232,240,0.9));backdrop-filter:blur(4px);"></div>
  {:else if option.value === 'pattern'}
    <div class="h-10 rounded-2xl border border-stone-950/10" style="background:radial-gradient(circle at 1px 1px,#94a3b8 1px,transparent 0);background-size:10px 10px;"></div>
  {:else if option.value === 'image'}
    <div class="h-10 rounded-2xl" style="background:linear-gradient(135deg,#1f2937,#64748b);"></div>
  {:else if option.value === 'fill'}
    <div class="h-10 rounded-2xl border border-stone-200" style="background:#ffffff;"></div>
  {:else}
    <div class="h-10 rounded-2xl bg-stone-200"></div>
  {/if}
{/snippet}

{#snippet patternPreview(option: RadioOption)}
  {@const url = patternDataUrl(option.value, '#0f172a')}
  <div
    class="h-10 rounded-2xl border border-stone-200"
    style={`background-image:${url};background-size:36px 36px;background-color:#f8fafc;`}
  ></div>
{/snippet}

{#snippet buttonPreview(option: RadioOption)}
  {#if option.value === 'glass'}
    <div class="flex h-10 items-center justify-center rounded-2xl border border-white/30 text-xs text-stone-900 dark:text-stone-100" style="background:rgba(255,255,255,0.18);backdrop-filter:blur(8px);">Aa</div>
  {:else if option.value === 'outline'}
    <div class="flex h-10 items-center justify-center rounded-2xl border-2 border-violet-600 bg-transparent text-xs text-violet-600">Aa</div>
  {:else}
    <div class="flex h-10 items-center justify-center rounded-2xl bg-stone-950 text-xs text-white">Aa</div>
  {/if}
{/snippet}

{#snippet radiusPreview(option: RadioOption)}
  {@const radius = ({ square: '0px', round: '8px', rounder: '16px', full: '9999px' } as Record<string, string>)[option.value] || '16px'}
  <div class="h-10 bg-stone-950" style={`border-radius:${radius};`}></div>
{/snippet}

{#snippet shadowPreview(option: RadioOption)}
  {@const shadow = ({
    none: 'none',
    soft: '0 8px 20px rgba(15,23,42,0.12)',
    strong: '0 14px 28px rgba(15,23,42,0.2)',
    hard: '4px 4px 0 rgba(15,23,42,0.8)',
  } as Record<string, string>)[option.value] || 'none'}
  <div class="h-10 rounded-2xl border border-stone-950/10 bg-white" style={`box-shadow:${shadow};`}></div>
{/snippet}

<div class="space-y-5">
  <TopBar
    pageTitle="Design"
    dirty={$themeDirty || profileDirty}
    {saving}
    {enhancing}
    canUndo={$canUndoTheme}
    canRedo={$canRedoTheme}
    onEnhance={enhanceTheme}
    onUndo={undoTheme}
    onRedo={redoTheme}
    onSave={saveTheme}
  />

  {#if message}
    <div class="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
      {message}
    </div>
  {/if}

  {#if error}
    <div class="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
      {error}
    </div>
  {/if}

  <div class="grid gap-5 xl:grid-cols-[88px_minmax(0,1fr)]">
    <SectionNav {sections} {activeSection} onSelect={selectSection} />

    <section class="editor-panel p-6">
      {#if activeSection === 'header'}
        <div class="space-y-6">
          <div>
            <p class="editor-label">Header</p>
            <h3 class="editor-title mt-2">프로필 헤더</h3>
            <p class="editor-desc mt-2">공개 프로필 상단에 표시되는 이름·소개와 헤더 레이아웃을 한 화면에서 조정합니다. 변경 사항은 상단 Save 버튼 한 번으로 저장됩니다.</p>
          </div>

          <div class="editor-section grid gap-4 md:grid-cols-2">
            <div class="space-y-4">
              <div class="editor-subhead">Profile identity</div>
              <label class="grid gap-2">
                <span class="editor-field-label">Display name</span>
                <input
                  type="text"
                  class="editor-input"
                  placeholder="표시 이름"
                  bind:value={profileDraft.display_name}
                  oninput={markProfileDirty}
                  onblur={commitProfileDraft}
                />
              </label>
              <label class="grid gap-2">
                <span class="editor-field-label">Bio</span>
                <textarea
                  rows="3"
                  maxlength="80"
                  class="editor-input"
                  placeholder="짧은 소개 (80자 이내)"
                  bind:value={profileDraft.bio}
                  oninput={markProfileDirty}
                  onblur={commitProfileDraft}
                ></textarea>
                <span class="editor-muted text-right">{profileDraft.bio.length} / 80</span>
              </label>
              <div class="editor-helper">@{$profile.username || 'username'} · 아바타 이미지와 username 변경은 Settings 탭에서 관리합니다.</div>
            </div>
            <div class="grid gap-4">
              <label class="grid gap-2">
                <span class="editor-field-label">Header layout</span>
                <select class="editor-input" value={$theme.header_layout || 'classic'} onchange={(e) => updateThemeField('header_layout', (e.currentTarget as HTMLSelectElement).value)}>
                  {#each headerLayoutOptions as option}
                    <option value={option.value}>{option.label}</option>
                  {/each}
                </select>
              </label>
              <label class="grid gap-2">
                <span class="editor-field-label">Title style</span>
                <select class="editor-input" value={$theme.title_style || 'text'} onchange={(e) => updateThemeField('title_style', (e.currentTarget as HTMLSelectElement).value)}>
                  {#each titleStyleOptions as option}
                    <option value={option.value}>{option.label}</option>
                  {/each}
                </select>
              </label>
              <label class="grid gap-2">
                <span class="editor-field-label">Accent color</span>
                <input type="color" class="editor-color-picker" value={$theme.accent_color || '#2563eb'} oninput={(e) => updateColor('accent_color', (e.currentTarget as HTMLInputElement).value)} />
                <span class="editor-helper">아바타 배경과 outline 버튼의 강조 색으로 사용됩니다.</span>
              </label>
            </div>
          </div>
        </div>
      {:else if activeSection === 'theme'}
        <div class="space-y-6">
          <div class="flex items-end justify-between gap-4">
            <div>
              <p class="editor-label">Theme</p>
              <h3 class="editor-title mt-2">프리셋 갤러리</h3>
            </div>
            <div class="editor-pill-group">
              <button type="button" class={`editor-pill ${presetTab === 'customizable' ? 'editor-pill-active' : ''}`} onclick={() => (presetTab = 'customizable')}>Customizable</button>
              <button type="button" class={`editor-pill ${presetTab === 'curated' ? 'editor-pill-active' : ''}`} onclick={() => (presetTab = 'curated')}>Curated</button>
            </div>
          </div>

          <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <button type="button" class="flex min-h-[228px] flex-col justify-between rounded-[1.75rem] border border-dashed border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 p-4 text-left transition hover:border-violet-400 hover:bg-violet-50" onclick={() => mergeTheme({ theme_preset_id: null })}>
              <div>
                <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-stone-100 dark:bg-stone-700 text-2xl text-stone-700 dark:text-stone-300">+</div>
                <h4 class="mt-4 text-base font-semibold text-stone-950 dark:text-stone-50">Custom</h4>
                <p class="editor-desc mt-2">현재 설정을 시작점으로 직접 조정합니다.</p>
              </div>
              <span class="editor-label">Start from scratch</span>
            </button>

            {#each visiblePresets as preset}
              {@const settings = preset.settings as Partial<EditorTheme>}
              {@const templateKey = ((preset.template_key || 'clean') as ProfileThemeName)}
              {@const previewTokens = resolveTheme(templateKey, settings)}
              {@const previewButtonBg = previewTokens['--button-bg'] || previewTokens['--accent'] || '#111827'}
              {@const previewButtonText = previewTokens['--button-text'] || previewTokens['--text-primary'] || '#ffffff'}
              {@const previewCardBg = previewTokens['--card-bg'] || '#ffffff'}
              {@const previewCardBorder = previewTokens['--card-border'] || 'transparent'}
              {@const previewCardText = previewTokens['--text-primary'] || '#111827'}
              {@const previewCardSubtext = previewTokens['--text-secondary'] || '#6b7280'}
              {@const previewAccent = previewTokens['--accent'] || '#2563eb'}
              {@const previewFooter = previewTokens['--footer-text'] || previewTokens['--text-tertiary'] || '#94a3b8'}
              {@const previewShadow = previewTokens['--card-shadow'] || 'none'}
              {@const previewRadius = previewTokens['--card-radius'] || '16px'}
              <button
                type="button"
                class={`overflow-hidden rounded-[1.75rem] border bg-white dark:bg-stone-800 text-left transition ${
                  $theme.theme_preset_id === preset.id
                    ? 'border-stone-950 ring-1 ring-stone-950'
                    : 'border-stone-200 dark:border-stone-700 hover:border-stone-300 dark:hover:border-stone-500 hover:shadow-md'
                }`}
                onclick={() => applyPresetLocally(preset)}
              >
                <div class="space-y-4 p-4" style={`background:${String(previewTokens['--page-bg'] || settings.bg_gradient || settings.bg_color || '#f8fafc')}; color:${String(previewCardText)};`}>
                  <div class="flex items-start justify-between gap-3">
                    <div>
                      <div class="text-[10px] font-semibold uppercase tracking-[0.18em]" style={`color:${String(previewFooter)};`}>Preview</div>
                      <div class="mt-2 text-3xl font-semibold" style={`font-family:${safeFontName(settings.font_heading) || 'Fraunces'};color:${String(settings.title_color || previewCardText)};`}>Aa</div>
                    </div>
                    <div class="mt-1 h-10 w-10 rounded-full border" style={`background:${String(previewAccent)};border-color:${String(previewCardBorder)};`}></div>
                  </div>

                  <div class="space-y-2.5 text-[10px] font-medium">
                    <div class="flex items-center gap-2">
                      <span class="w-12 shrink-0 tracking-[0.04em]" style={`color:${String(previewFooter)};`}>버튼</span>
                      <div class="flex h-9 flex-1 items-center justify-center rounded-2xl border px-3" style={`background:${String(previewButtonBg)};color:${String(previewButtonText)};border-color:${String(previewTokens['--button-border'] || previewCardBorder)};box-shadow:${String(previewTokens['--button-shadow'] || previewShadow)};`}>
                        Primary CTA
                      </div>
                    </div>

                    <div class="flex items-center gap-2">
                      <span class="w-12 shrink-0 tracking-[0.04em]" style={`color:${String(previewFooter)};`}>카드</span>
                      <div class="flex flex-1 items-center justify-between rounded-2xl border px-3 py-2" style={`background:${String(previewCardBg)};border-color:${String(previewCardBorder)};box-shadow:${String(previewShadow)};border-radius:${String(previewRadius)};`}>
                        <div class="min-w-0">
                          <div class="truncate text-[11px] font-semibold" style={`color:${String(previewCardText)};`}>Link surface</div>
                          <div class="truncate text-[10px]" style={`color:${String(previewCardSubtext)};`}>Secondary text</div>
                        </div>
                        <div class="ml-3 h-2.5 w-2.5 rounded-full" style={`background:${String(previewAccent)};`}></div>
                      </div>
                    </div>

                    <div class="flex items-center gap-2">
                      <span class="w-12 shrink-0 tracking-[0.04em]" style={`color:${String(previewFooter)};`}>포인트</span>
                      <div class="flex flex-1 items-center gap-2 rounded-2xl border px-3 py-2" style={`background:${String(previewCardBg)};border-color:${String(previewCardBorder)};border-radius:${String(previewRadius)};`}>
                        <div class="h-5 w-5 rounded-full border" style={`background:${String(previewAccent)};border-color:${String(previewCardBorder)};`}></div>
                        <div class="min-w-0">
                          <div class="truncate text-[10px] font-semibold" style={`color:${String(previewCardText)};`}>Accent / badge tone</div>
                          <div class="truncate text-[10px]" style={`color:${String(previewFooter)};`}>Footer / helper tone</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="flex items-center justify-between px-4 py-3">
                  <div>
                    <div class="text-sm font-semibold text-stone-950 dark:text-stone-50">{preset.name}</div>
                    <div class="editor-muted uppercase tracking-[0.18em]">{preset.category}</div>
                  </div>
                  {#if $theme.theme_preset_id === preset.id}
                    <span class="rounded-full bg-emerald-100 px-2 py-1 text-[10px] font-semibold text-emerald-700">Selected</span>
                  {/if}
                </div>
              </button>
            {/each}
          </div>
        </div>
      {:else if activeSection === 'wallpaper'}
        <div class="space-y-6">
          <div>
            <p class="editor-label">Wallpaper</p>
            <h3 class="editor-title mt-2">배경과 카드 표면</h3>
          </div>

          <RadioCardGroup value={$theme.wallpaper_style || 'fill'} options={wallpaperOptions} columns={5} onChange={changeWallpaperStyle} preview={wallpaperPreview} />

          {#if ($theme.wallpaper_style || 'fill') === 'fill'}
            <label class="editor-section grid gap-2">
              <span class="editor-field-label">Background color</span>
              <input type="color" class="editor-color-picker" value={$theme.bg_color || '#ffffff'} oninput={(e) => updateColor('bg_color', (e.currentTarget as HTMLInputElement).value)} />
            </label>
          {:else if ($theme.wallpaper_style || 'fill') === 'gradient'}
            {@const gradientControls = parseGradientControls($theme.bg_gradient)}
            <div class="editor-section grid gap-4 md:grid-cols-2">
              <div class="space-y-4">
                <div>
                  <div class="editor-field-label">Gradient colors</div>
                  <div class="mt-3 grid gap-3 sm:grid-cols-2">
                    <label class="grid gap-2">
                      <span class="editor-helper font-medium">Start</span>
                      <input type="color" class="editor-color-picker" value={gradientControls.from} oninput={(e) => updateGradientControl('from', (e.currentTarget as HTMLInputElement).value)} />
                    </label>
                    <label class="grid gap-2">
                      <span class="editor-helper font-medium">End</span>
                      <input type="color" class="editor-color-picker" value={gradientControls.to} oninput={(e) => updateGradientControl('to', (e.currentTarget as HTMLInputElement).value)} />
                    </label>
                  </div>
                </div>

                <label class="grid gap-2">
                  <span class="editor-field-label">Direction</span>
                  <select class="editor-input" value={String(gradientControls.angle)} onchange={(e) => updateGradientControl('angle', Number((e.currentTarget as HTMLSelectElement).value))}>
                    {#each gradientDirectionOptions as option}
                      <option value={String(option.value)}>{option.label}</option>
                    {/each}
                  </select>
                </label>

                <details class="editor-details rounded-xl">
                  <summary class="cursor-pointer text-sm font-medium text-stone-700 dark:text-stone-300">Advanced CSS</summary>
                  <label class="mt-3 grid gap-2">
                    <span class="editor-helper font-medium">Custom linear-gradient()</span>
                    <textarea class="editor-input min-h-28" value={$theme.bg_gradient || 'linear-gradient(135deg, #1d4ed8 0%, #7c3aed 100%)'} oninput={(e) => updateThemeField('bg_gradient', (e.currentTarget as HTMLTextAreaElement).value)}></textarea>
                  </label>
                </details>
              </div>

              <div class="space-y-3">
                <div class="rounded-[1.25rem] border border-stone-200 dark:border-stone-700" style={`min-height:220px;background:${$theme.bg_gradient || 'linear-gradient(135deg, #1d4ed8 0%, #7c3aed 100%)'};`}></div>
                <p class="editor-helper leading-5">색상 2개와 방향만 바꾸면 대부분의 배경은 충분합니다. 특수한 효과가 필요할 때만 Advanced CSS를 사용하세요.</p>
              </div>
            </div>
          {:else if ($theme.wallpaper_style || 'fill') === 'blur'}
            <div class="editor-section space-y-3">
              <label class="grid gap-2">
                <span class="editor-field-label">Base color</span>
                <input type="color" class="editor-color-picker" value={$theme.bg_color || '#e2e8f0'} oninput={(e) => updateColor('bg_color', (e.currentTarget as HTMLInputElement).value)} />
              </label>
              <p class="editor-helper leading-5">Blur는 카드 뒤에 frosted glass 효과를 얹습니다. 단색 배경 위에 은은한 depth를 주고 싶을 때 사용하세요.</p>
            </div>
          {:else if ($theme.wallpaper_style || 'fill') === 'pattern'}
            <div class="editor-section space-y-4">
              <label class="grid gap-2">
                <span class="editor-field-label">Base color</span>
                <input type="color" class="editor-color-picker" value={$theme.bg_color || '#f8fafc'} oninput={(e) => updateColor('bg_color', (e.currentTarget as HTMLInputElement).value)} />
              </label>
              <RadioCardGroup value={$theme.wallpaper_pattern || patternOptions[0]?.value} options={patternOptions} columns={4} onChange={(value) => updateThemeField('wallpaper_pattern', value)} preview={patternPreview} />
              <label class="grid gap-2">
                <span class="editor-field-label">Pattern accent</span>
                <input type="color" class="editor-color-picker" value={$theme.accent_color || '#2563eb'} oninput={(e) => updateColor('accent_color', (e.currentTarget as HTMLInputElement).value)} />
              </label>
            </div>
          {:else}
            <div class="editor-section space-y-4">
              <label class="grid gap-2">
                <span class="editor-field-label">Image URL</span>
                <input type="url" class="editor-input" value={$theme.bg_image_url || ''} oninput={(e) => updateThemeField('bg_image_url', (e.currentTarget as HTMLInputElement).value)} placeholder="https://..." />
              </label>

              {#if $wallpaperLibrary.length > 0}
                <div>
                  <div class="mb-1 flex items-baseline justify-between">
                    <span class="editor-field-label">큐레이션 라이브러리</span>
                    <span class="editor-muted uppercase tracking-[0.18em]">{$wallpaperLibrary.length} backgrounds</span>
                  </div>
                  <p class="editor-helper mb-3 leading-5">프리셋의 배경 이미지만 빌려옵니다. 색상 · 폰트 · 버튼 스타일은 현재 디자인 그대로 유지돼요.</p>
                  <div class="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-5">
                    {#each $wallpaperLibrary as item (item.id)}
                      <button
                        type="button"
                        class={`group relative aspect-square overflow-hidden rounded-xl border-2 transition ${
                          $theme.bg_image_url === item.bgImageUrl
                            ? 'border-stone-950 ring-2 ring-stone-950/20'
                            : 'border-stone-200 dark:border-stone-600 hover:border-stone-400 dark:hover:border-stone-500'
                        }`}
                        title={item.name}
                        onclick={() => updateThemeField('bg_image_url', item.bgImageUrl)}
                      >
                        <img src={item.bgImageUrl} alt={item.name} loading="lazy" class="h-full w-full object-cover transition group-hover:scale-105" />
                        <span class="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-stone-950/80 to-transparent px-2 py-1 text-left text-[10px] font-medium text-white">
                          {item.name}
                        </span>
                        {#if $theme.bg_image_url === item.bgImageUrl}
                          <span class="absolute right-1 top-1 rounded-full bg-stone-950 px-1.5 py-0.5 text-[9px] font-semibold text-white">✓</span>
                        {/if}
                      </button>
                    {/each}
                  </div>
                </div>
              {/if}
            </div>
          {/if}

          {#if advancedCardSurfaceEnabled}
            <details class="editor-section" bind:open={advancedWallpaper}>
              <summary class="cursor-pointer text-sm font-semibold text-stone-900 dark:text-stone-100">Advanced card surface</summary>
              <div class="mt-4 grid gap-4 md:grid-cols-2">
                <label class="grid gap-2">
                  <span class="editor-field-label">Card surface color</span>
                  <input type="color" class="editor-color-picker" value={$theme.card_bg_color || '#ffffff'} oninput={(e) => updateColor('card_bg_color', (e.currentTarget as HTMLInputElement).value)} />
                </label>
                <label class="grid gap-2">
                  <span class="flex items-center justify-between editor-field-label">
                    <span>Card translucency</span>
                    <span class="editor-helper">{Math.round(($theme.card_opacity ?? 0.88) * 100)}%</span>
                  </span>
                  <!-- 범위를 서버가 안 줬으면 슬라이더를 그리지 않는다. 브라우저 기본 0~100 으로
                       그리면 서버가 조용히 clamp 하는 값을 사용자가 끌게 된다. -->
                  {#if cardOpacityRange}
                    <input type="range" min={cardOpacityRange.min} max={cardOpacityRange.max} step="0.05" value={$theme.card_opacity ?? 0.88} oninput={(e) => updateThemeField('card_opacity', Number((e.currentTarget as HTMLInputElement).value))} />
                  {/if}
                </label>
              </div>
              <p class="editor-helper mt-3 leading-5">그라디언트 · Blur · 이미지 배경에서만 frosted glass 효과가 눈에 띕니다. Fill/Pattern에서는 카드 색상이 있는 그대로 적용됩니다.</p>
            </details>
          {:else}
            <div class="editor-callout">
              Advanced card surface는 <strong>Gradient · Blur · Image</strong> 배경에서만 의미 있는 frosted glass 효과를 만듭니다. 다른 스타일에서는 카드 색이 그대로 표시됩니다.
            </div>
          {/if}
        </div>
      {:else if activeSection === 'text'}
        <div class="space-y-6">
          <div>
            <p class="editor-label">Text</p>
            <h3 class="editor-title mt-2">타이포그래피</h3>
          </div>

          <div class="grid gap-4 md:grid-cols-2">
            <label class="editor-section grid gap-2">
              <span class="editor-field-label">Page font</span>
              <select class="editor-input" value={$theme.font_body || 'Pretendard'} onchange={(e) => updateThemeField('font_body', (e.currentTarget as HTMLSelectElement).value)}>
                {#each bodyFontOptions as option}
                  <option value={option}>{option}</option>
                {/each}
              </select>
            </label>

            <label class="editor-section grid gap-2">
              <span class="editor-field-label">Title font</span>
              <select class="editor-input" value={$theme.font_heading || 'Fraunces'} onchange={(e) => updateThemeField('font_heading', (e.currentTarget as HTMLSelectElement).value)}>
                {#each headingFontOptions as option}
                  <option value={option}>{option}</option>
                {/each}
              </select>
            </label>

            <label class="editor-section grid gap-2">
              <span class="flex items-center justify-between editor-field-label">
                <span>Page text color</span>
                <ContrastBadge foreground={$theme.text_color || '#0f172a'} background={effectivePageBg} />
              </span>
              <input type="color" class="editor-color-picker" value={$theme.text_color || '#0f172a'} oninput={(e) => updateColor('text_color', (e.currentTarget as HTMLInputElement).value)} />
              {#if contrastNote}<span class="editor-muted leading-3">{contrastNote}</span>{/if}
            </label>

            <label class="editor-section grid gap-2">
              <span class="flex items-center justify-between editor-field-label">
                <span>Title color</span>
                <ContrastBadge foreground={$theme.title_color || $theme.text_color || '#0f172a'} background={effectivePageBg} largeText />
              </span>
              <input type="color" class="editor-color-picker" value={$theme.title_color || $theme.text_color || '#0f172a'} oninput={(e) => updateColor('title_color', (e.currentTarget as HTMLInputElement).value)} />
            </label>
          </div>

          <div class="editor-section grid gap-4 md:grid-cols-2">
            <label class="grid gap-2">
              <span class="editor-field-label">Title size</span>
              <select class="editor-input" value={$theme.title_size || 'small'} onchange={(e) => updateThemeField('title_size', (e.currentTarget as HTMLSelectElement).value)}>
                {#each titleSizeOptions as option}
                  <option value={option.value}>{option.label}</option>
                {/each}
              </select>
            </label>
            <label class="flex items-center gap-3 editor-input">
              <input type="checkbox" checked={$theme.title_font_override ?? false} onchange={(e) => updateThemeField('title_font_override', (e.currentTarget as HTMLInputElement).checked)} />
              <span class="editor-field-label">Alternative title font 사용</span>
            </label>
          </div>
        </div>
      {:else if activeSection === 'buttons'}
        <div class="space-y-6">
          <div>
            <p class="editor-label">Buttons</p>
            <h3 class="editor-title mt-2">버튼 스타일 3축 분리</h3>
          </div>

          <div class="editor-section space-y-5">
            <div>
              <div class="editor-field-label mb-3">Button style</div>
              <RadioCardGroup value={$theme.button_variant || 'solid'} options={buttonVariantOptions} onChange={(value) => updateThemeField('button_variant', value)} preview={buttonPreview} />
            </div>

            <div>
              <div class="editor-field-label mb-3">Corner roundness</div>
              <RadioCardGroup value={$theme.button_radius_preset || 'rounder'} options={radiusOptions} columns={4} onChange={(value) => updateThemeField('button_radius_preset', value)} preview={radiusPreview} />
            </div>

            <div>
              <div class="editor-field-label mb-3">Button shadow</div>
              <RadioCardGroup value={$theme.button_shadow || 'none'} options={shadowOptions} columns={4} onChange={(value) => updateThemeField('button_shadow', value)} preview={shadowPreview} />
            </div>

            <div class="grid gap-4 md:grid-cols-2">
              <label class="grid gap-2">
                <span class="editor-field-label">Button color</span>
                <input type="color" class="editor-color-picker" value={$theme.button_color || '#111827'} oninput={(e) => updateColor('button_color', (e.currentTarget as HTMLInputElement).value)} />
              </label>
              <label class="grid gap-2">
                <span class="flex items-center justify-between editor-field-label">
                  <span>Button text color</span>
                  <ContrastBadge foreground={$theme.button_text_color || '#ffffff'} background={$theme.button_color || '#111827'} largeText />
                </span>
                <input type="color" class="editor-color-picker" value={$theme.button_text_color || '#ffffff'} oninput={(e) => updateColor('button_text_color', (e.currentTarget as HTMLInputElement).value)} />
              </label>
            </div>
          </div>
        </div>
      {:else}
        <div class="space-y-6">
          <div>
            <p class="editor-label">Footer</p>
            <h3 class="editor-title mt-2">브랜딩 표시</h3>
          </div>

          <label class="editor-section flex items-center justify-between px-5 py-4">
            <div>
              <div class="text-sm font-semibold text-stone-950 dark:text-stone-50">Hide AX branding</div>
              <div class="editor-desc mt-1">공개 프로필 하단의 AX Hub 브랜딩을 숨깁니다.</div>
            </div>
            <input type="checkbox" class="h-5 w-5 rounded border-stone-300" checked={$theme.hide_branding ?? false} onchange={(e) => updateThemeField('hide_branding', (e.currentTarget as HTMLInputElement).checked)} />
          </label>
        </div>
      {/if}
    </section>
  </div>
</div>

<!-- Color picker styles moved to application.css as .editor-color-picker -->
